/**
 * Error handling utilities for API errors
 *
 * Turns axios errors into ApiError instances whose `message` is ready to
 * show to a visitor. The mapping follows public API contract v1 §4:
 * switch on HTTP status first and `error.code` second; an unrecognised
 * code falls back to its status class (4xx: the server's message, 5xx: a
 * generic "something went wrong").
 *
 * @module errorHandler
 */

import ApiError, { ERROR_CODES } from './ApiError';
import config from '../../config';

const CONTACT_EMAIL = config.business.contactEmail;

/**
 * User-facing messages for codes where the frontend knows better wording
 * than a generic server message (or where the server sends none).
 */
export const ERROR_MESSAGES = Object.freeze({
  [ERROR_CODES.SLOT_UNAVAILABLE]: 'That time was just taken, please pick another.',
  [ERROR_CODES.NOTICE_PERIOD]:
    'That time is too soon to book online. Please choose a later time.',
  [ERROR_CODES.RATE_LIMITED]: 'Too many requests. Please wait a minute and try again.',
  [ERROR_CODES.CALENDAR_UNAVAILABLE]:
    `We can't check the calendar right now. Please try again in a few minutes, or email ${CONTACT_EMAIL}.`,
  [ERROR_CODES.SERVICE_UNAVAILABLE]: 'This service is temporarily unavailable. Please try again later.',
  [ERROR_CODES.INTERNAL_ERROR]: 'Something went wrong on our side. Please try again.',
  [ERROR_CODES.NETWORK_ERROR]:
    'Unable to connect to the server. Please check your internet connection.',
  [ERROR_CODES.TIMEOUT]: 'The request timed out. Please check your connection and try again.',
  [ERROR_CODES.CANCELED]: 'The request was cancelled.',
  [ERROR_CODES.INVALID_RESPONSE]: 'Something went wrong on our side. Please try again.',
  [ERROR_CODES.VALIDATION_ERROR]: 'Please check your details and try again.',
  [ERROR_CODES.UNAUTHORIZED]: 'Please sign in to continue.',
  [ERROR_CODES.NOT_FOUND]: 'The requested resource was not found.',
  [ERROR_CODES.CONFLICT]: 'That web name is already used. Please choose another.',
  [ERROR_CODES.CONTENT_CONFLICT]:
    'This was changed somewhere else (another tab or device). Reload it to see the latest version, then make your change again.',
  [ERROR_CODES.MEDIA_IN_USE]: 'This file is still used on the site, so it cannot be deleted.',
  [ERROR_CODES.MEDIA_QUOTA_EXCEEDED]: 'The media library is full. Delete files you no longer use, then try again.',
  [ERROR_CODES.MEDIA_TOO_LARGE]: 'That file is too large. Images and PDFs can be up to 10 MB.',
  [ERROR_CODES.MEDIA_TYPE_NOT_ALLOWED]: 'That file type is not allowed. Use a JPEG, PNG or WebP image, or a PDF.',
});

/**
 * Codes whose frontend wording always wins over the server's message.
 * notice_period and validation_error keep the server message because it
 * carries the specific rule that failed (e.g. the configured notice hours).
 */
const FRONTEND_WORDING_WINS = new Set([
  ERROR_CODES.SLOT_UNAVAILABLE,
  ERROR_CODES.RATE_LIMITED,
  ERROR_CODES.CALENDAR_UNAVAILABLE,
  ERROR_CODES.SERVICE_UNAVAILABLE,
  ERROR_CODES.INTERNAL_ERROR,
]);

/** Code to assume when the body carries none (e.g. an nginx 502 page). */
const codeForStatus = (status) => {
  switch (status) {
    case 400: return ERROR_CODES.VALIDATION_ERROR;
    case 401: return ERROR_CODES.UNAUTHORIZED;
    case 404: return ERROR_CODES.NOT_FOUND;
    case 405: return ERROR_CODES.METHOD_NOT_ALLOWED;
    case 409: return ERROR_CODES.SLOT_UNAVAILABLE;
    // The edge proxy answers an oversized upload with a bare 413 (no envelope).
    case 413: return ERROR_CODES.MEDIA_TOO_LARGE;
    case 415: return ERROR_CODES.MEDIA_TYPE_NOT_ALLOWED;
    case 422: return ERROR_CODES.NOTICE_PERIOD;
    case 429: return ERROR_CODES.RATE_LIMITED;
    default:
      return status >= 500 ? ERROR_CODES.INTERNAL_ERROR : null;
  }
};

/**
 * Pick the message to show for a status/code pair.
 *
 * @param {number} status
 * @param {string|null} code
 * @param {string|null} serverMessage
 * @returns {string}
 */
export const mapErrorMessage = (status, code, serverMessage) => {
  if (code && FRONTEND_WORDING_WINS.has(code)) {
    return ERROR_MESSAGES[code];
  }
  if (status >= 500) {
    // Contract §4: 5xx never shows server text.
    return ERROR_MESSAGES[code] || ERROR_MESSAGES[ERROR_CODES.INTERNAL_ERROR];
  }
  if (serverMessage) {
    return serverMessage;
  }
  return ERROR_MESSAGES[code] || 'Something went wrong. Please try again.';
};

/**
 * Read `{success:false, error:{code, message, error_id}}` from a body.
 * Tolerates a missing or non-JSON body (proxy error pages).
 */
const readEnvelopeError = (body) => {
  const err = body && typeof body === 'object' ? body.error : null;
  if (err && typeof err === 'object') {
    return {
      code: typeof err.code === 'string' ? err.code : null,
      message: typeof err.message === 'string' ? err.message : null,
      errorId: typeof err.error_id === 'string' ? err.error_id : null,
      field: typeof err.field === 'string' ? err.field : null,
    };
  }
  return { code: null, message: null, errorId: null, field: null };
};

/**
 * Transform an axios error (or anything thrown inside a request) into an ApiError.
 *
 * @param {Error} error
 * @returns {ApiError}
 */
export const handleApiError = (error) => {
  if (error?.isApiError) {
    return error;
  }

  // Aborted via AbortController (hook cleanup). Callers ignore these.
  if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
    return new ApiError(ERROR_MESSAGES[ERROR_CODES.CANCELED], 0, ERROR_CODES.CANCELED);
  }

  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
    return new ApiError(ERROR_MESSAGES[ERROR_CODES.TIMEOUT], 0, ERROR_CODES.TIMEOUT);
  }

  if (!error?.response) {
    return new ApiError(ERROR_MESSAGES[ERROR_CODES.NETWORK_ERROR], 0, ERROR_CODES.NETWORK_ERROR);
  }

  const { status, data } = error.response;
  const envelope = readEnvelopeError(data);
  const code = envelope.code || codeForStatus(status);

  return new ApiError(mapErrorMessage(status, code, envelope.message), status, code, {
    errorId: envelope.errorId,
    serverMessage: envelope.message,
    field: envelope.field,
  });
};

/**
 * Get a user-friendly message from any thrown value.
 *
 * @param {Error|ApiError|string|unknown} error
 * @returns {string}
 */
export const getErrorMessage = (error) => {
  if (error?.isApiError) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message || 'An unexpected error occurred.';
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Log an error in development. Production reporting goes through
 * utils/sentry (captureException) where the caller wants it.
 *
 * @param {Error|ApiError} error
 * @param {object} [context]
 */
export const logError = (error, context = {}) => {
  if (config.isDevelopment) {
    console.error('Error occurred:', {
      error: error?.isApiError ? error.toJSON() : error,
      context,
    });
  }
};

/** @returns {boolean} True when `error` is an ApiError with this code */
export const isErrorCode = (error, code) => Boolean(error?.isApiError && error.code === code);

/** @returns {boolean} True when `error` is an ApiError with this status */
export const isErrorStatus = (error, status) => Boolean(error?.isApiError && error.status === status);

/** 409 slot_unavailable: refetch availability and ask for another time. */
export const isSlotUnavailableError = (error) =>
  isErrorCode(error, ERROR_CODES.SLOT_UNAVAILABLE) || isErrorStatus(error, 409);

/** 400 validation_error */
export const isValidationError = (error) => isErrorCode(error, ERROR_CODES.VALIDATION_ERROR);

/** 401 */
export const isAuthError = (error) => isErrorStatus(error, 401);

/** 404 */
export const isNotFoundError = (error) => isErrorStatus(error, 404);

/** 5xx */
export const isServerError = (error) => Boolean(error?.isApiError && error.isServerError());

/** No response received */
export const isNetworkError = (error) =>
  Boolean(error?.isApiError && error.isNetworkError() && !error.isCanceled);

/** Aborted by an AbortController */
export const isCanceledError = (error) => Boolean(error?.isApiError && error.isCanceled);
